<script setup lang="ts">
import { computed } from 'vue'
import { useHead } from '@unhead/vue'
import type { Resolved } from '@/types'
import { getService, relatedServiceLinks, settings, PATHS } from '@/content'
import { baseHead, breadcrumbLd } from '@/lib/seo'
import ProBadge from '@/components/ProBadge.vue'
import CtaProjet from '@/components/CtaProjet.vue'
import ZonesIntervention from '@/components/ZonesIntervention.vue'
import LabelsBand from '@/components/LabelsBand.vue'
import MatterportEmbed from '@/components/MatterportEmbed.vue'

const props = defineProps<{ resolved: Resolved }>()
const home = computed(() => props.resolved.service)

const NAV = {
  depannage: '/depannage-menuiserie-habitat-mayenne-laval-la-baule/',
  menuiserie: '/menuiserie-mayenne/',
  agencement: '/agencement-menuiserie-mayenne-la-baule-laval/',
  couverture: '/couverture-ardoise-zinc-bac-acier-velux-mayenne/',
  ossature: '/ossature-bois-mayenne/',
  isolation: '/isolation-mayenne/',
  entretien: '/entretien-bois-mayenne/',
}

function img(path: string, fallback = ''): string {
  return getService(path)?.seo?.og_image || fallback
}

// Image de partage (og:image) : on garde l'original, adapte aux reseaux sociaux.
const heroImg = computed(() => home.value?.seo?.og_image || img(NAV.menuiserie))

// Variantes WebP responsives servies en 1st-party (generees au build dans public/opt/,
// cf. scripts/gen-images.mjs). Chaque appareil telecharge une image proche de sa taille
// d'affichage : gros gain sur le hero (LCP) et les cartes de sections.
const OPT = '/opt'
const heroOpt = {
  src: `${OPT}/home-hero-828.webp`,
  srcset: `${OPT}/home-hero-640.webp 640w, ${OPT}/home-hero-828.webp 828w, ${OPT}/home-hero-1024.webp 1024w`,
}
function cardImg(name: string) {
  return {
    src: `${OPT}/${name}-560.webp`,
    srcset: `${OPT}/${name}-400.webp 400w, ${OPT}/${name}-560.webp 560w, ${OPT}/${name}-760.webp 760w`,
  }
}
// Cartes : pleine largeur (moins le padding) en mobile, ~560px en 2 colonnes au-dela de 860px.
const cardSizes = '(min-width: 861px) 560px, calc(100vw - 40px)'
const sectionImg = {
  depannage: cardImg('depannage'),
  menuiserie: cardImg('menuiserie'),
  agencement: cardImg('agencement'),
}

const depannageLinks = computed(() => relatedServiceLinks(NAV.depannage).slice(0, 8))

const menuiserieBlocks = [
  { path: '/menuiserie-mayenne/menuiserie-exterieure/fenetres-porte-fenetre-baie-coulissante-galandage-mayenne/', title: 'Fenêtres, portes-fenêtres et baies vitrées', text: "Remplacement et pose de fenêtres en PVC, aluminium ou bois : isolation renforcée, confort et sécurité au quotidien." },
  { path: '/menuiserie-mayenne/menuiserie-exterieure/portes-de-garage-mayenne-laval-la-baule/', title: 'Portes de garage', text: 'Installation, rénovation et motorisation de portes de garage sectionnelles, basculantes ou enroulables.' },
  { path: '/menuiserie-mayenne/menuiserie-exterieure/volets-roulants-mayenne-laval-la-baule/', title: 'Volets roulants', text: 'Volets roulants manuels ou motorisés, pour améliorer le confort, la sécurité et l\'isolation de votre habitat.' },
  { path: '/menuiserie-mayenne/menuiserie-exterieure/porte-dentree/', title: "Portes d'entrée", text: "Des portes d'entrée alliant sécurité, performance thermique et esthétique, adaptées à votre façade." },
]

const savoirFaire = computed(() => [
  { title: 'Couverture et étanchéité', text: "Couverture ardoise, zinc, bac acier, tuiles, gouttières et fenêtres de toit. Une expertise complète pour votre toiture.", path: NAV.couverture },
  { title: 'Ossature bois et structures', text: 'Extensions, surélévations, abris, garages et carports en ossature bois. Des solutions durables et performantes.', path: NAV.ossature },
  { title: 'Isolation thermique et phonique', text: 'Isolation des combles, murs, planchers et rampants pour réduire vos consommations et améliorer votre confort.', path: NAV.isolation },
  { title: 'Entretien et interventions ciblées', text: 'Nettoyage, entretien et réparations ponctuelles sur menuiseries, terrasses, bardages et toitures.', path: NAV.entretien },
])

useHead(() => ({
  ...baseHead({
    title: home.value?.seo?.seo_title || settings.site_name,
    description: home.value?.seo?.seo_description || home.value?.intro,
    canonical: '/',
    image: heroImg.value,
  }),
  script: [breadcrumbLd([{ label: 'Accueil', path: '/' }])],
}))
</script>

<template>
  <div class="home">
    <!-- Hero -->
    <section class="hhero">
      <img
        :src="heroOpt.src"
        :srcset="heroOpt.srcset"
        sizes="100vw"
        width="1024"
        height="1536"
        alt=""
        class="hhero__bg"
        fetchpriority="high"
        aria-hidden="true"
      />
      <div class="hhero__overlay"></div>
      <div class="container hhero__in">
        <div class="hhero__content">
          <h1>{{ home?.h1 || 'Menuiserie, Agencement et Dépannage de l\'habitat à Mayenne, Laval et La Baule' }}</h1>
          <p>{{ home?.intro }}</p>
          <div class="hhero__cta">
            <router-link :to="NAV.depannage" class="btn btn--primary">Dépannage : intervention rapide</router-link>
            <router-link :to="PATHS.contact" class="btn btn--outline hhero__outline">Étude gratuite de votre projet</router-link>
          </div>
        </div>
      </div>
      <ProBadge class="hhero__pro" />
    </section>

    <!-- Dépannage -->
    <section class="section section--alt">
      <div class="container split">
        <div class="split__media">
          <img :src="sectionImg.depannage.src" :srcset="sectionImg.depannage.srcset" :sizes="cardSizes" alt="Dépannage de l'habitat par les Ateliers du Terras" loading="lazy" width="560" height="420" />
        </div>
        <div class="split__text">
          <span class="eyebrow">Intervention rapide et maîtrisée</span>
          <h2>Dépannage de l'habitat</h2>
          <p>Un souci sur vos fermetures, votre toiture ou vos menuiseries ? Nous intervenons vite et bien, à Mayenne, Laval et La Baule.</p>
          <ul class="checklist checklist--cols">
            <li v-for="d in depannageLinks" :key="d.path">
              <router-link :to="d.path">{{ d.label }}</router-link>
            </li>
          </ul>
          <router-link :to="NAV.depannage" class="btn btn--primary">Voir le dépannage</router-link>
        </div>
      </div>
    </section>

    <!-- Menuiserie -->
    <section class="section">
      <div class="container split split--reverse">
        <div class="split__media">
          <img :src="sectionImg.menuiserie.src" :srcset="sectionImg.menuiserie.srcset" :sizes="cardSizes" alt="Menuiserie et fermetures sur mesure" loading="lazy" width="560" height="420" />
        </div>
        <div class="split__text">
          <span class="eyebrow">Menuiserie et fermeture</span>
          <h2>Experts en menuiserie et fermeture</h2>
          <p>Nous accompagnons vos projets de rénovation et d'amélioration de l'habitat avec des solutions fiables, durables et adaptées à chaque configuration.</p>
          <div class="subblocks">
            <div v-for="b in menuiserieBlocks" :key="b.path" class="subblock">
              <h3>{{ b.title }}</h3>
              <p>{{ b.text }}</p>
              <router-link :to="b.path" class="link-arrow">Découvrir →</router-link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Agencement -->
    <section class="section section--alt">
      <div class="container split">
        <div class="split__media">
          <img :src="sectionImg.agencement.src" :srcset="sectionImg.agencement.srcset" :sizes="cardSizes" alt="Agencement intérieur et fabrication sur mesure" loading="lazy" width="560" height="420" />
        </div>
        <div class="split__text">
          <span class="eyebrow">Fabrication dans notre atelier</span>
          <h2>Agencement intérieur et fabrication sur mesure</h2>
          <p>L'agencement fait partie intégrante de notre ADN. Dans notre atelier, nous concevons et fabriquons des aménagements intérieurs sur mesure, pensés pour optimiser les espaces et répondre à vos usages du quotidien.</p>
          <ul class="checklist">
            <li>Cuisines, dressings et rangements sur mesure</li>
            <li>Meubles de salle de bain, meubles TV et bibliothèques</li>
            <li>Meuble sous escalier et aménagements spécifiques</li>
          </ul>
          <router-link :to="NAV.agencement" class="btn btn--primary">Découvrir l'agencement</router-link>
        </div>
      </div>
    </section>

    <!-- Savoir-faire complet -->
    <section class="section">
      <div class="container">
        <span class="eyebrow">Un savoir-faire complet pour l'habitat</span>
        <h2>Au-delà des menuiseries et fermetures, nous intervenons sur toute l'enveloppe du bâtiment</h2>
        <div class="grid grid--2 sf">
          <div v-for="s in savoirFaire" :key="s.path" class="sf__card">
            <div class="sf__body">
              <h3>{{ s.title }}</h3>
              <p>{{ s.text }}</p>
            </div>
            <router-link :to="s.path" class="btn btn--primary btn--block">En savoir plus</router-link>
          </div>
        </div>
      </div>
    </section>

    <ZonesIntervention />

    <CtaProjet />

    <LabelsBand />

    <MatterportEmbed title="Découvrez notre showroom en 3D" />
  </div>
</template>

<style scoped>
.hhero {
  position: relative;
  min-height: 520px;
  display: flex;
  align-items: center;
  overflow: hidden;
}
.hhero__bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.hhero__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(20, 24, 18, 0.78) 0%, rgba(20, 24, 18, 0.55) 45%, rgba(20, 24, 18, 0.2) 100%);
}
.hhero__in {
  position: relative;
  padding-top: 60px;
  padding-bottom: 60px;
}
.hhero__content {
  max-width: 640px;
  color: #fff;
}
.hhero__content h1 {
  color: #fff;
  font-size: clamp(1.8rem, 4.2vw, 2.9rem);
}
.hhero__content p {
  font-size: 1.1rem;
  color: #eef0ec;
}
.hhero__cta {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 24px;
}
.hhero__outline {
  color: #fff;
  border-color: #fff;
}
.hhero__outline:hover {
  background: #fff;
  color: var(--title);
}
.hhero__pro {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 2;
}

.split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 44px;
  align-items: center;
}
.split--reverse .split__media {
  order: 2;
}
.split__media img {
  width: 100%;
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  aspect-ratio: 4 / 3;
  object-fit: cover;
}
.checklist--cols {
  columns: 2;
  column-gap: 24px;
}
.checklist--cols li a {
  color: var(--title);
  font-weight: 600;
}
.checklist--cols li a:hover {
  color: var(--green-dark);
  text-decoration: none;
}

.subblocks {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 8px;
}
.subblock h3 {
  font-size: 1.02rem;
  margin-bottom: 4px;
}
.subblock p {
  font-size: 0.9rem;
  color: var(--muted);
  margin-bottom: 6px;
}
.link-arrow {
  color: var(--green-dark);
  font-weight: 700;
  font-size: 0.88rem;
}

.sf {
  margin-top: 22px;
}
.sf__card {
  background: #fff;
  border: 1px solid var(--border);
  border-top: 3px solid var(--green);
  border-radius: var(--radius);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: var(--shadow-sm);
}
.sf__body h3 {
  color: var(--title);
}
.sf__body p {
  color: var(--ink);
  margin: 0;
}
.sf__card .btn {
  margin-top: auto;
}

@media (max-width: 860px) {
  .split,
  .subblocks {
    grid-template-columns: 1fr;
    gap: 24px;
  }
  .split--reverse .split__media {
    order: -1;
  }
  .checklist--cols {
    columns: 1;
  }
}
@media (max-width: 560px) {
  /* Le titre commence sous le badge PRO pour eviter tout chevauchement. */
  .hhero {
    align-items: flex-start;
  }
  .hhero__in {
    padding-top: 92px;
  }
  .hhero__pro {
    top: 12px;
    right: 12px;
  }
}
</style>
